import { createFileRoute } from "@tanstack/react-router";
import MakeModelPage from "@/pages/MakeModelPage";
import { routeHead } from "@/lib/seo/route-head";
import { listingsV2QueryOptions } from "@/lib/queries/publicData";
import { getModelPage, matchesModelPage, modelHeading } from "@/data/modelPages";
import { isEligibleCityListing } from "@/utils/cityArea";
import { countMakeModel, makeModelRobots } from "@/utils/makeModelFilter";

type ModelLoaderData = { conditions: Array<string | null> | null; count: number } | null;

/** Zero matching listings → noindex in the server HTML; canonical stays. */
function withRobots<T extends { meta: Array<Record<string, string>> }>(head: T, count: number | null | undefined): T {
  const robots = makeModelRobots(count);
  return robots ? { ...head, meta: [...head.meta.filter((m) => m.name !== "robots"), { name: "robots", content: robots }] } : head;
}

export const Route = createFileRoute("/cars/$make/$model/")({
  head: ({ params, loaderData }) => {
    const base = routeHead("/cars/$make/$model/", params as Record<string, string>);
    const page = getModelPage(params.make, params.model);
    const data = (loaderData ?? null) as unknown as ModelLoaderData;
    if (!data) return base;
    if (!page || !data.conditions) return withRobots(base, data.count);
    // Same rule as the H1 in MakeModelPage, computed from the same stock.
    const { title } = modelHeading(page, data.conditions);
    const swap: Record<string, string> = { title: "title", "og:title": "property", "twitter:title": "name", "title-meta": "name" };
    return withRobots({
      ...base,
      meta: base.meta.map((m) => {
        if ("title" in m && Object.keys(m).length === 1) return { title };
        if (m.name === "title" || m.name === "twitter:title" || m.property === "og:title") return { ...m, content: title };
        if (m.name === "description" || m.property === "og:description" || m.name === "twitter:description")
          return { ...m, content: page.description };
        return m;
      }),
      ...(swap ? {} : {}),
    }, data.count);
  },
  loader: async ({ context, params }): Promise<ModelLoaderData> => {
    // Curated model pages render their listings in the initial HTML. The query
    // must match MakeModelPage (useListingsV2) exactly to share the cache key.
    const page = getModelPage(params.make, params.model);
    const make = params.make.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    const rows = await context.queryClient
      .ensureQueryData(listingsV2QueryOptions({ country_code: "RO", make, limit: 200 }))
      .catch(() => undefined);
    if (!Array.isArray(rows)) return null;
    const count = countMakeModel(rows as never[], { make: params.make, model: params.model });
    const conditions = page
      ? rows.filter((r: any) => isEligibleCityListing(r) && matchesModelPage(page, r)).map((r: any) => (r.condition as string | null) ?? null)
      : null;
    return { conditions, count };
  },
  component: RouteComponent,
});

function RouteComponent() {
  return (
    <MakeModelPage />
  );
}
